/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useMutation } from '@apollo/client';
import { Switch } from '@coursera-web/cds-labs';

import user from 'js/lib/user';

import { InlineNotification, Typography2 } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { receivedEmailPreferences } from 'bundles/account-settings/actions/SettingsServerActionCreators';
import { UpdateStates } from 'bundles/account-settings/constants/SettingsConstants';
import type {
  UpdateGlobalOptOutMutationResult,
  UpdateGlobalOptOutMutationVariables,
} from 'bundles/account-settings/queries';
import { UpdateGlobalOptOutMutation } from 'bundles/account-settings/queries';
import SettingsStore from 'bundles/account-settings/stores/SettingsStore';
import type { UpdateStatus } from 'bundles/account-settings/types/EmailPreferences';
import useDebounce from 'bundles/common/hooks/useDebounce';
import { getStatusMessages } from 'bundles/user-account/utils/errorMessageUtils';

import _t from 'i18n!nls/account-settings';

type Props = {
  preference?: boolean;
  onChange: (value: boolean) => void;
};

const styles = {
  header: css`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--cds-spacing-200);
  `,
  textColumn: css`
    flex: 1;
    min-width: 0;
  `,
  title: css`
    margin-block-end: var(--cds-spacing-50);
  `,
  switchWrapper: css`
    flex-shrink: 0;
  `,
  inlineNotification: css`
    margin-block-start: var(--cds-spacing-200);
  `,
};

const GlobalEmailNotificationPreference = ({ onChange, preference = false }: Props) => {
  const userId = user.get().id.toString();
  const initialPreferenceRef = React.useRef(preference);
  const [isOptedOut, setIsOptedOut] = React.useState<boolean>(() => preference);
  const [preferenceUpdateStatus, setPreferenceUpdateStatus] = React.useState<UpdateStatus>({
    state: UpdateStates.IDLE,
  });

  const track = useTracker();
  const switchRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_checkbox', {
    checkbox: {
      name: 'global_email_notifications_preference',
      checked: isOptedOut,
    },
  });

  const [updateEmailGlobalPreferences] = useMutation<
    UpdateGlobalOptOutMutationResult,
    UpdateGlobalOptOutMutationVariables
  >(UpdateGlobalOptOutMutation, {
    onCompleted: (data) => {
      const global = data?.UpdateGlobalOptOut?.global;
      const notificationPreferences = SettingsStore.getNotificationPreferences();
      const emailPreferences = SettingsStore.getEmailPreferences();

      setPreferenceUpdateStatus({ state: UpdateStates.SUCCEEDED });
      receivedEmailPreferences({ ...notificationPreferences, global }, emailPreferences);
    },
    onError: () => {
      setPreferenceUpdateStatus({ state: UpdateStates.FAILED });
      onChange(initialPreferenceRef.current);
      setIsOptedOut(initialPreferenceRef.current);
    },
  });

  // Debounce because Switch has no loading indicator. Disabling during save would leave the user with no visual feedback.
  const toggleEmailPreferences = useDebounce(() => {
    setPreferenceUpdateStatus({ state: UpdateStates.UPDATING });
    updateEmailGlobalPreferences({ variables: { userId, body: {}, enabled: !isOptedOut } });
  }, 500);

  const handleToggleEmailPreferences = () => {
    onChange(!isOptedOut);
    setIsOptedOut((prevOptedOut) => !prevOptedOut);
    track('click_checkbox', {
      checkbox: {
        name: 'global_email_notifications_preference',
        checked: !isOptedOut,
      },
    });
    toggleEmailPreferences();
  };

  return (
    <div ref={switchRef}>
      <div css={styles.header}>
        <div css={styles.textColumn}>
          <Typography2 component="h2" variant="subtitleLarge" css={styles.title}>
            {_t('Email notifications')}
          </Typography2>
          <Typography2 component="p" variant="bodySecondary" color="supportText">
            {_t(
              'Select the type of email notifications you want to opt in and out of, or optionally disable all email notifications.'
            )}
          </Typography2>
        </div>
        <div css={styles.switchWrapper}>
          <Switch
            aria-label={_t('Email notifications')}
            selected={!isOptedOut}
            onToggle={handleToggleEmailPreferences}
          />
        </div>
      </div>
      <div role="status" aria-atomic="true">
        {preferenceUpdateStatus.state === UpdateStates.SUCCEEDED && (
          <InlineNotification css={styles.inlineNotification} severity="success">
            {getStatusMessages.UPDATE_SUCCEEDED}
          </InlineNotification>
        )}
      </div>
      <div role="alert" aria-atomic="true">
        {preferenceUpdateStatus.state === UpdateStates.FAILED && (
          <InlineNotification css={styles.inlineNotification} severity="error">
            {getStatusMessages.UPDATE_FAILED}
          </InlineNotification>
        )}
      </div>
    </div>
  );
};

export default GlobalEmailNotificationPreference;
